/**
 * Data Import — the catalog of Meadow reference entities an administrator can bulk-import from a file
 * (CSV / TSV / Excel) via the pict-section-dataimport wizard.
 *
 * The list is derived from the platform Master List seed (the same Entity / Label / Icon the rest of
 * the app uses) — EVERY master list is an import target, minus a small exclude set of entities that are
 * not backed by a normal importable table. Because the wizard is schema-driven (it fetches
 * `/1.0/<Entity>/Schema` and lets the admin map spreadsheet columns to fields), any real Meadow entity
 * works; the only thing that would break is a catalog name with no `/Schema` endpoint (see the exclude
 * set below). Some master lists are "context-dependent" — a Sample, Line Item, Bid Item, Lot, Core,
 * Test Instance, Mix Design, Test Specification / Requirement belongs to a parent (Project, Contract,
 * Material, …), so a standalone import is only meaningful when the source file carries the parent FK
 * id column(s); they are still offered (the admin maps those columns), but flat catalogs (Materials,
 * Pay Items, Test Specification Sets, …) import with no parent. Pure data + helpers — no pict / REST.
 */

const libMasterLists = require('../master-lists/Headlight-MasterLists.js');

// Reserved: master-list entries to keep OUT of the import targets — for any list not backed by a real
// importable Meadow table (no live `/1.0/<Entity>/Schema` + create endpoint, so the wizard would have
// nothing to map). Currently EMPTY — every master list, SampleStatus included, resolves a live schema
// endpoint and imports through the same schema-driven wizard. Add a future synthesized / non-table
// master list here if one is ever added to the catalog.
const IMPORT_EXCLUDE = new Set();

/**
 * Build the import targets from EVERY master list (in catalog order), minus IMPORT_EXCLUDE, as
 * { Entity, Label, Icon, Description }. New master lists flow through automatically.
 * @returns {Array<{Entity:string, Label:string, Icon:string, Description:string}>}
 */
function buildImportEntities()
{
	return (Array.isArray(libMasterLists) ? libMasterLists : [])
		.filter((pEntry) => pEntry && pEntry.Entity && !IMPORT_EXCLUDE.has(pEntry.Entity))
		.map((pEntry) =>
			({
				Entity: pEntry.Entity,
				Label: pEntry.Label || pEntry.Entity,
				Icon: pEntry.Icon || 'fas fa-table',
				Description: pEntry.Description || '',
			}));
}

// The context entities (Project, Contract, ...) are also importable — both so an admin can bulk-load
// them and so they're available as the PARENTS of interconnected child imports (the GUID-strategy panel
// references them). Stable abbreviations + sizes live in Import-Context-Catalog.js.
const CONTEXT_IMPORT_ENTITIES =
[
	{ Entity: 'Project', Label: 'Projects', Icon: 'fas fa-diagram-project', Description: 'Projects — a context parent for interconnected imports (Line Items, Samples, …).' },
	{ Entity: 'Contract', Label: 'Contracts', Icon: 'fas fa-file-signature', Description: 'Contracts — a context parent for interconnected imports.' },
	{ Entity: 'Sample', Label: 'Samples', Icon: 'fas fa-vial', Description: 'Field / material samples.' },
	{ Entity: 'PhysicalAsset', Label: 'Physical Assets', Icon: 'fas fa-cubes', Description: 'Physical assets / infrastructure inventory.' },
	{ Entity: 'Inspection', Label: 'Inspections', Icon: 'fas fa-clipboard-check', Description: 'Inspections.' },
];

const IMPORT_ENTITIES = (() =>
{
	const tmpList = buildImportEntities();
	const tmpSeen = {};
	tmpList.forEach((pEntity) => { tmpSeen[pEntity.Entity] = true; });
	CONTEXT_IMPORT_ENTITIES.forEach((pEntity) => { if (!tmpSeen[pEntity.Entity]) { tmpList.push(pEntity); } });
	return tmpList;
})();

/**
 * Look up a curated import entity by name.
 * @param {string} pEntityName
 * @returns {{Entity:string, Label:string, Icon:string, Description:string}|null}
 */
function findEntity(pEntityName)
{
	return IMPORT_ENTITIES.find((pEntity) => pEntity.Entity === pEntityName) || null;
}

module.exports =
{
	IMPORT_EXCLUDE,
	IMPORT_ENTITIES,
	buildImportEntities,
	findEntity,
};
